/* eslint-disable */
/**
 * offlineSchedule.service.js
 * Service for explicit offline downloading, IndexedDB caching, and retrieval
 * of upcoming schedules, rehearsals, performances, categories, and linked playlists.
 */

import dbService from '@/service/db.service';
import ScheduleService from '@/service/schedule.service';
import offlinePlaylistService from '@/service/offlinePlaylist.service';
import { appEvents } from '@/store/app.events';

const SCHEDULE_EVENTS_KEY = 'triad_offline_schedule_events';
const SCHEDULE_CATEGORIES_KEY = 'triad_offline_schedule_categories';
const SCHEDULE_META_KEY = 'triad_offline_schedule_meta';

class OfflineScheduleService {
    constructor() {
        this.isDownloading = false;
    }

    /**
     * Downloads upcoming schedule events, categories, and optionally linked playlists
     * for offline access (default: 35 days ahead from today).
     */
    async downloadUpcomingSchedule({ daysAhead = 35, downloadLinkedPlaylists = true, onProgress = null } = {}) {
        if (this.isDownloading) {
            console.warn('[OfflineScheduleService] Schedule download already in progress.');
            return false;
        }

        this.isDownloading = true;
        appEvents.emit('offline:schedule-download-started');

        try {
            const today = new Date();
            // Start from 7 days ago to cover recent week + upcoming days
            const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
            const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + daysAhead, 23, 59, 59, 999);

            const params = {
                from: startDate.toISOString(),
                to: endDate.toISOString()
            };

            if (onProgress) onProgress({ step: 'events', message: 'Buscando eventos da agenda...' });

            // 1. Fetch categories
            let categories = [];
            try {
                categories = await ScheduleService.getCategories();
                if (Array.isArray(categories)) {
                    await dbService.setItem(SCHEDULE_CATEGORIES_KEY, categories);
                }
            } catch (catErr) {
                console.warn('[OfflineScheduleService] Error fetching categories:', catErr);
            }

            // 2. Fetch events in date range
            const res = await ScheduleService.list(params);
            const eventList = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);

            // 3. For each event, prefetch and bundle participants and linked playlists
            const hydratedEvents = [];
            const linkedPlaylistIds = new Set();

            for (let i = 0; i < eventList.length; i++) {
                const ev = eventList[i];
                const evId = ev._id || ev.id;

                let participants = ev.participants || [];
                if (evId) {
                    try {
                        const partRes = await ScheduleService.fetchParticipants(evId);
                        if (Array.isArray(partRes?.data)) {
                            participants = partRes.data;
                        } else if (Array.isArray(partRes)) {
                            participants = partRes;
                        }
                    } catch (e) {
                        // ignore participant error
                    }
                }

                // Collect linked playlists
                if (Array.isArray(ev.playlists)) {
                    ev.playlists.forEach(p => {
                        const pId = p.playlist_id || p.id || p._id;
                        if (pId) linkedPlaylistIds.add(String(pId));
                    });
                }

                hydratedEvents.push({
                    ...ev,
                    participants
                });

                if (onProgress) {
                    onProgress({
                        step: 'events_progress',
                        current: i + 1,
                        total: eventList.length,
                        message: `Processando evento ${i + 1} de ${eventList.length}...`
                    });
                }
            }

            // Save hydrated events list into IndexedDB
            await dbService.setItem(SCHEDULE_EVENTS_KEY, hydratedEvents);

            // 4. Download linked playlists if requested
            if (downloadLinkedPlaylists && linkedPlaylistIds.size > 0) {
                const playlistIds = Array.from(linkedPlaylistIds);
                for (let idx = 0; idx < playlistIds.length; idx++) {
                    const pId = playlistIds[idx];
                    if (onProgress) {
                        onProgress({
                            step: 'playlists',
                            current: idx + 1,
                            total: playlistIds.length,
                            message: `Baixando repertório vinculado ${idx + 1} de ${playlistIds.length}...`
                        });
                    }
                    try {
                        await offlinePlaylistService.downloadPlaylist({ _id: pId, id: pId });
                    } catch (pErr) {
                        console.warn(`[OfflineScheduleService] Failed downloading linked playlist ${pId}:`, pErr);
                    }
                }
            }

            // Save download metadata
            const meta = {
                downloadedAt: Date.now(),
                eventCount: hydratedEvents.length,
                range: {
                    from: startDate.toISOString(),
                    to: endDate.toISOString()
                },
                linkedPlaylistsCount: linkedPlaylistIds.size
            };
            await dbService.setItem(SCHEDULE_META_KEY, meta);

            appEvents.emit('offline:schedule-downloaded', meta);
            return { success: true, count: hydratedEvents.length, meta };
        } catch (error) {
            console.error('[OfflineScheduleService] Error downloading schedule:', error);
            throw error;
        } finally {
            this.isDownloading = false;
        }
    }

    /**
     * Retrieves cached schedule events from IndexedDB.
     */
    async getCachedEvents() {
        try {
            const events = await dbService.getItem(SCHEDULE_EVENTS_KEY);
            return Array.isArray(events) ? events : [];
        } catch (e) {
            console.warn('[OfflineScheduleService] Error loading cached schedule events:', e);
            return [];
        }
    }

    /**
     * Retrieves cached schedule categories from IndexedDB.
     */
    async getCachedCategories() {
        try {
            const categories = await dbService.getItem(SCHEDULE_CATEGORIES_KEY);
            return Array.isArray(categories) ? categories : [];
        } catch (e) {
            console.warn('[OfflineScheduleService] Error loading cached categories:', e);
            return [];
        }
    }

    /**
     * Checks if schedule data is stored offline and returns metadata.
     */
    async getScheduleMeta() {
        try {
            return await dbService.getItem(SCHEDULE_META_KEY);
        } catch (e) {
            return null;
        }
    }

    /**
     * Clears offline schedule cache.
     */
    async clearScheduleCache() {
        try {
            await dbService.removeItem(SCHEDULE_EVENTS_KEY);
            await dbService.removeItem(SCHEDULE_CATEGORIES_KEY);
            await dbService.removeItem(SCHEDULE_META_KEY);
            appEvents.emit('offline:schedule-cleared');
            return true;
        } catch (e) {
            console.warn('[OfflineScheduleService] Error clearing schedule cache:', e);
            return false;
        }
    }
}

export const offlineScheduleService = new OfflineScheduleService();
export default offlineScheduleService;
